"use client";

import { useAtomValue } from "jotai";
import { useCopilotTreatments } from "@zapier/ai-toolkit/client";
import { sessionAtom } from "../account";
import type { Profile } from "@zapier/ai-toolkit";

/**
 * Hook to check if Copilot is disabled due to the "NoPilot" experiment flag.
 *
 * Returns true if the experimentNoPilot feature flag is set to "copilot_off",
 * indicating that Copilot should be disabled for this account.
 *
 * @returns boolean - true if Copilot is disabled by the No Pilot experiment flag
 */
export function useIsCopilotNoPilotDisabled(): boolean | undefined {
  const session = useAtomValue(sessionAtom);

  // Build profile from session to avoid circular dependency with copilot-tools
  // This matches the implementation in copilot-tools/src/index.ts profileAtom
  const profile: Profile = {
    email: session?.email || "",
    is_logged_in: Boolean(session?.is_logged_in),
    is_staff: Boolean(session?.is_staff),
    signup: session?.signup || "",
    ...(session && {
      id: Number(session.id),
      current_account_id: Number(session.current_account_id),
    }),
  } as Profile;

  const { ready, featureFlags } = useCopilotTreatments(profile);

  if (!ready) return;

  return featureFlags.experimentNoPilot === "copilot_off";
}
